import {
  checkTransmittalAccessRequest,
  getTransmittalAttachedFilesRequest,
  getTransmittalDataByIdRequest,
  getTransmittalFileVersionsForSpaceRequest,
  saveTransmittalsDataRequest,
} from "@Features/transmittals/api/transmittals.api";
import {
  setTransmitalsData,
  setTransmitalMetadata,
  setTransmittalAccess,
  setTransmittalVersions,
  setTransmittalDataForReviews,
  setTransmittalReferences,
  mergeTransmitalsData,
} from "../state/transmitals.reducer";
import type {
  GetFilesAttachedToTransmittalRequest,
  GetTransmittalFileVersionsForSpaceRequest,
  IsTransmittal403Request,
  SaveTransmittalsRequest,
} from "@ServerTypes/transmittals/transmittals.schema";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import type { RootState } from "src/reducers";
import { objectIdString } from "@Helpers/schemas/common.schema";

type AppDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

export async function saveTransmitalsData(data: SaveTransmittalsRequest) {
  return await saveTransmittalsDataRequest(data);
}

export async function getTransmittalAttachedFiles(data: GetFilesAttachedToTransmittalRequest) {
  return await getTransmittalAttachedFilesRequest(data);
}

export function getTransmittalAccess(data: IsTransmittal403Request) {
  return async (dispatch: AppDispatch) => {
    const responseData = await checkTransmittalAccessRequest(data);
    if (responseData.data.status) {
      dispatch(setTransmittalAccess(responseData.data));
    }
  };
}

export {
  setTransmitalsData,
  setTransmitalMetadata,
  setTransmittalAccess,
  setTransmittalDataForReviews,
  setTransmittalReferences,
  mergeTransmitalsData,
};

export function getTransmittalFileVersionsForSpace(data: GetTransmittalFileVersionsForSpaceRequest) {
  return async (dispatch: AppDispatch) => {
    const responseData = await getTransmittalFileVersionsForSpaceRequest(data);
    dispatch(setTransmittalVersions(responseData.data));
  };
}

export function getTransmittals(params: { transmittalVersionId?: string }) {
  return async (dispatch: AppDispatch) => {
    // Entry point: this action's public contract takes a plain `string` because DesignViewer.tsx
    // calls it with a raw `URLSearchParams.get("transmittalId")` value, so the id is validated here
    // rather than branded. `getTransmittalDataById` requires a 24-hex id and answers anything else
    // with a validation error this action never surfaced anyway, so a doomed request is not issued.
    const transmittalId = objectIdString.safeParse(params.transmittalVersionId).data;
    if (!transmittalId) {
      return;
    }
    await getTransmittalDataByIdRequest({ transmittalId }).then((response) => {
      if (response.data.status) {
        dispatch(setTransmitalsData(response.data.data));
        if (response.data.metadata) {
          dispatch(setTransmitalMetadata(response.data.metadata));
        }
      }
    });
  };
}
